import { put, takeLatest, call } from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import UrlHelper from 'helpers/url'
import NoticeHelper from 'helpers/notice'
import * as ApiRouter from 'constants/apiRouting'
import { SHOW_NOTICE } from '../notice'
import {
  FETCH_TASKS_TABLE_DATA,
  FETCH_TASKS_TABLE_DATA_SUCCESS,
  FETCH_TASKS_TABLE_DATA_FAILURE,
  FETCH_WORKFLOW_LIST,
  FETCH_WORKFLOW_LIST_SUCCESS,
  FETCH_WORKFLOW_LIST_FAILURE,
  FETCH_TASK_FORM_DATA,
  FETCH_TASK_FORM_DATA_SUCCESS,
  FETCH_TASK_FORM_DATA_FAILURE,
  FETCH_TASK_POLICIES_LIST,
  FETCH_TASK_POLICIES_LIST_SUCCESS,
  FETCH_TASK_POLICIES_LIST_FAILURE,
  TASKS_TABLE_POSITION_UPDATE_FETCH,
  TASKS_TABLE_POSITION_UPDATE_SUCCESS,
  LOAD_TASK_TABLE_DATA,
  CHANGE_TASK,
  DELETE_TASK,
  FETCH_TASK_ASSIGN_LIST,
  FETCH_TASK_ASSIGN_LIST_SUCCESS,
  FETCH_TASK_ASSIGN_LIST_FAILURE,
  DELETE_TASK_ASSIGN,
  CHANGE_TASK_ASSIGN,
  FETCH_TASK_INPUTS_TREE,
  FETCH_TASK_INPUTS_TREE_SUCCESS,
  FETCH_TASK_INPUTS_TREE_FAILURE,
  SET_IS_INPUTS_TREE_FETCHING,
} from './reducer'

function* fetchTasksDataList({ payload }) {
  try {
    yield put({ type: LOAD_TASK_TABLE_DATA, payload: true })
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRouter.JOB_WORKFLOW_TASKS_TABLE}${UrlHelper.getPsfsQuery(
        payload,
      )}`,
    })

    if (!response) {
      yield put({ type: FETCH_TASKS_TABLE_DATA_FAILURE })
    }

    yield put({ type: FETCH_TASKS_TABLE_DATA_SUCCESS, payload: response })
    yield put({ type: LOAD_TASK_TABLE_DATA, payload: false })
  } catch (error) {
    console.log('FETCH TASK LIST ERROR', error)
  }
}

function* fetchWorkflows({ payload }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRouter.MY_JOBS_WORKFLOWS}${UrlHelper.getPsfsQuery(
        payload,
      )}&admin`,
    })

    if (response && !response.error) {
      yield put({ type: FETCH_WORKFLOW_LIST_SUCCESS, payload: response })
    } else {
      yield put({ type: FETCH_WORKFLOW_LIST_FAILURE })
    }
  } catch (error) {
    console.log('FETCH WORKFLOW LIST FAILURE ', error)
  }
}

function* fetchTaskFormData({ payload: { entityId } }) {
  try {
    let formData = {}

    if (entityId) {
      formData = yield call(fetchFromAPI, {
        headers: getDefaultAutorizedHeaders(),
        partUrl: UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_INFO, {
          id: entityId,
        }),
      })
    }

    if (Object.keys(formData).length) {
      yield put({
        type: FETCH_TASK_FORM_DATA_SUCCESS,
        formData,
      })
    } else {
      yield put({
        type: FETCH_TASK_FORM_DATA_FAILURE,
      })
    }
  } catch (error) {
    console.log('FETCH TASK FORMDATA ERROR', error)
  }
}

function* fetchTaskPoliciesList() {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.JOB_WORKFLOW_TASK_POLICIES,
    })

    if (response && !response.error) {
      yield put({ type: FETCH_TASK_POLICIES_LIST_SUCCESS, payload: response })
    } else {
      yield put({ type: FETCH_TASK_POLICIES_LIST_FAILURE })
    }
  } catch (error) {
    console.log('FETCH TASK POLICIES ERROR', error)
  }
}

function* fetchTaskListPositionUpdate({ payload: { params, data, index } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PATCH',
      partUrl: UrlHelper.create(ApiRouter.JOB_WORKFLOW_POSITION_CHANGE, params),
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(
          'Job Task position successfully changed',
        ),
      })
      yield put({
        type: TASKS_TABLE_POSITION_UPDATE_SUCCESS,
        payload: {
          index,
          position: data.position,
        },
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createWarningObj('Sorry, something went wrong'),
      })
    }
  } catch (error) {
    console.log('JOB TASK POSITION UPDATE ERROR', error)
  }
}

function* changeTask({ payload: { entityId, values }, promise: { resolve } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: !entityId ? 'POST' : 'PUT',
      partUrl: !entityId
        ? ApiRouter.JOB_WORKFLOW_TASKS
        : UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_UPDATE, {
            id: entityId,
          }),
      data: values,
    })

    if (response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(
          Object.values(response.errors || {}) || response.message,
        ),
      })

      return yield call(resolve, response.errors)
    }

    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createSuccessObj(
        `Task ${entityId === 0 ? 'created' : 'changed'} successfully`,
      ),
    })

    return yield call(resolve, false)
  } catch (error) {
    throw new Error(`UNKNOWN TASK CHANGE ERROR. DATA: ${error}`)
  }
}

function* fetchTaskInputTree({ payload: { taskId } }) {
  try {
    yield put({ type: SET_IS_INPUTS_TREE_FETCHING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'GET',
      partUrl: UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_INPUT_TREE, {
        id: taskId,
      }),
    })

    if (response && !response.error) {
      yield put({ type: FETCH_TASK_INPUTS_TREE_SUCCESS, payload: response })
    } else {
      yield put({ type: FETCH_TASK_INPUTS_TREE_FAILURE })
    }

    yield put({ type: SET_IS_INPUTS_TREE_FETCHING, payload: false })
  } catch (error) {
    console.error('FETCH JOB TASK INPUT ERROR', error)
  }
}

function* deleteTask(action) {
  const { entityId } = action.payload
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_UPDATE, {
        id: entityId,
      }),
    })

    if (response === 204) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('Task deleted successfully'),
      })

      return yield fetchTasksDataList(action)
    }

    console.log('TASK DELETE UNKNOWN ERROR. RESPONSE: ', response)
  } catch (error) {
    console.log('TASK DELETE ERROR', error)
  }
}

function* fetchTaskAssignList({ payload: { id, psfs } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_ASSIGNMENTS, {
        id,
      })}${UrlHelper.getPsfsQuery({ psfs })}`,
    })

    if (!response) {
      yield put({ type: FETCH_TASK_ASSIGN_LIST_FAILURE })
    }

    yield put({ type: FETCH_TASK_ASSIGN_LIST_SUCCESS, payload: response })
  } catch (error) {
    console.log('ASSIGN LIST UPDATE ERROR', error)
  }
}

function* changeTaskAssign({
  payload: { id, siteId, data, psfs, withFetch = true },
}) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: !siteId ? 'POST' : 'PUT',
      partUrl: !siteId
        ? UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_ASSIGNMENTS, {
            id,
          })
        : UrlHelper.create(ApiRouter.JOB_WORKFLOW_TASK_ASSIGNMENTS_UPDATE, {
            id,
            siteId,
          }),
      data,
    })

    if (withFetch && !response.error) {
      yield put({ type: FETCH_TASK_ASSIGN_LIST, payload: { id, psfs } })
    }
  } catch (error) {
    throw new Error(`UNKNOWN TASK CHANGE ERROR. DATA: ${error}`)
  }
}

function* deleteTaskAssign({ payload: { id, siteId, psfs } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: UrlHelper.create(
        ApiRouter.JOB_WORKFLOW_TASK_ASSIGNMENTS_UPDATE,
        {
          id,
          siteId,
        },
      ),
    })

    if (!response.error) {
      yield put({ type: FETCH_TASK_ASSIGN_LIST, payload: { id, psfs } })
    }
  } catch (error) {
    console.log('TASK DELETE ERROR', error)
  }
}

function* jobTasksAdminSaga() {
  yield takeLatest(FETCH_TASKS_TABLE_DATA, fetchTasksDataList)
  yield takeLatest(FETCH_WORKFLOW_LIST, fetchWorkflows)
  yield takeLatest(FETCH_TASK_FORM_DATA, fetchTaskFormData)
  yield takeLatest(FETCH_TASK_POLICIES_LIST, fetchTaskPoliciesList)
  yield takeLatest(
    TASKS_TABLE_POSITION_UPDATE_FETCH,
    fetchTaskListPositionUpdate,
  )
  yield takeLatest(CHANGE_TASK, changeTask)
  yield takeLatest(DELETE_TASK, deleteTask)
  yield takeLatest(FETCH_TASK_ASSIGN_LIST, fetchTaskAssignList)
  yield takeLatest(CHANGE_TASK_ASSIGN, changeTaskAssign)
  yield takeLatest(DELETE_TASK_ASSIGN, deleteTaskAssign)
  yield takeLatest(FETCH_TASK_INPUTS_TREE, fetchTaskInputTree)
}

export default jobTasksAdminSaga
